import React, { useState } from "react";
import {
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import "./StripePaymentModal.css";
import { useCanViewPrices } from "../../utils/pricingVisibility";

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: "16px",
      color: "#495057",
      fontFamily: "Poppins, sans-serif",
      "::placeholder": {
        color: "#aab7c4",
      },
    },
    invalid: {
      color: "#dc3545",
    },
  },
};

const StripePaymentModal = ({ onClose, onSuccess, amount, billingDetails }) => {
  const showPrices = useCanViewPrices();
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) return;

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError("Card element not found. Please try again.");
      return;
    }

    setIsProcessing(true);
    setError(null);

    // Prefer PaymentMethod creation (modern Stripe flow); fallback to token for legacy backends.
    const billingDetailsPayload = billingDetails?.name || billingDetails?.address
      ? {
          name: billingDetails.name || undefined,
          address: billingDetails.address?.line1 || billingDetails.address?.city
            ? {
                line1: billingDetails.address.line1 || undefined,
                line2: billingDetails.address.line2 || undefined,
                city: billingDetails.address.city || undefined,
                state: billingDetails.address.state || undefined,
                postal_code: billingDetails.address.postal_code || undefined,
                country: billingDetails.address.country || undefined,
              }
            : undefined,
        }
      : undefined;

    const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement,
      billing_details: billingDetailsPayload,
    });
    if (pmError) {
      console.error("[Stripe] createPaymentMethod error", pmError);
    } else if (paymentMethod?.id) {
      console.log("[Stripe] PaymentMethod created", {
        paymentMethodId: paymentMethod.id,
        amount,
      });
    }

    if (!pmError && paymentMethod?.id) {
      setIsProcessing(false);
      onSuccess?.({
        id: paymentMethod.id,
        kind: "payment_method",
        card: paymentMethod.card,
        billing_details: paymentMethod.billing_details,
        raw: paymentMethod,
      });
      onClose();
      return;
    }

    const { error: tokenError, token } = await stripe.createToken(cardElement, billingDetailsPayload);
    if (tokenError) {
      console.error("[Stripe] createToken fallback error", tokenError);
    } else if (token?.id) {
      console.log("[Stripe] Token created (fallback)", {
        tokenId: token.id,
        amount,
      });
    }
    if (tokenError) {
      setError(pmError?.message || tokenError.message || "Unable to process payment details.");
      setIsProcessing(false);
      return;
    }

    setIsProcessing(false);
    onSuccess?.({
      ...token,
      kind: "token",
      raw: token,
    });
    onClose();
  };

  return (
    <div className="stripe-modal-overlay" onClick={onClose}>
      <div
        className="stripe-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="stripe-modal-header">
          <h2 className="stripe-modal-title">Complete Payment with Stripe</h2>
          {showPrices && (
            <div className="stripe-modal-amount">
              Amount: ${amount ? amount.toFixed(2) : "0.00"}
            </div>
          )}
          {billingDetails?.formatted && (
            <div className="stripe-modal-billing">
              <span className="stripe-modal-billing-label">Billing: </span>
              <span className="stripe-modal-billing-value">{billingDetails.formatted}</span>
            </div>
          )}
          <button
            type="button"
            className="stripe-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="stripe-modal-form">
          <p className="stripe-modal-card-hint">Enter your card number, expiry date, and CVC below.</p>
          <div className="stripe-element-wrapper">
            <CardElement options={CARD_ELEMENT_OPTIONS} />
          </div>

          {error && <div className="stripe-modal-error">{error}</div>}

          <div className="stripe-modal-buttons">
            <button
              type="button"
              className="stripe-modal-cancel-btn"
              onClick={onClose}
              disabled={isProcessing}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="stripe-modal-pay-btn"
              disabled={!stripe || isProcessing}
            >
              {isProcessing ? "Processing..." : "Pay Now"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StripePaymentModal;
